import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  Slide,
  Snackbar,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  ArrowDownward,
  ArrowUpward,
  DashboardOutlined,
  MoneyOffOutlined,
  MoneyOutlined,
  Person,
  ShoppingCart
} from "@material-ui/icons";

import useStyles from "./styles";
import { useTheme } from "@material-ui/core/styles";
import React, { useState } from "react";
import { AttachMoney, ContentCopy, Info } from "@mui/icons-material";
import { Barcode } from "../Barcode";
import { OrderUnit } from "../../models/Order";
import { Product } from "../../models/Product";
import * as lab from "@material-ui/lab";
import { Currency } from "../misc/Currency";
import { SecuredField } from "../../views/users/misc/SecuredField";
import { StoreTable } from "../../views/tenants/Products/StoreTable";
import { useI18n } from "../../hooks/useI18n";

interface ProductDetailSectionProps {
  product: any;
  mobile: boolean;
}

export const ProductDetailSection = ({
  product,
  mobile
}: ProductDetailSectionProps) => {
  const theme = useTheme();
  const classes = useStyles();
  const { t } = useI18n();

  const [copyToClipboardNotification, setCopyToClipboardNotification] =
    useState({ open: false, msg: "" });

  const renderProductDetails = (
    propertyName: string,
    value: any,
    secured = false,
    style: any = {}
  ) => (
    <Grid container className={classes.productDetailsItem}>
      <Grid item xs={6} className={classes.detailsCell}>
        <div className={classes.detailsNameColumn}>{propertyName}</div>
      </Grid>
      <Grid item xs={6} className={classes.detailsCell}>
        {secured ? (
          <div className={classes.detailsDataColumn}>
            <SecuredField>{value}</SecuredField>
          </div>
        ) : (
          <div className={classes.detailsDataColumn} style={style}>
            {value}
          </div>
        )}
      </Grid>
    </Grid>
  );

  const renderGrossMarginal = (product: Product) => {
    let directionIcon =
      product.pricing.grossPriceMarginalPercentage > 0 ? (
        <ArrowUpward />
      ) : (
        <ArrowDownward />
      );
    return (
      <Box
        display={"flex"}
        alignItems={"center"}
        color={
          product.pricing.grossPriceMarginalPercentage > 0
            ? theme.palette.success.dark
            : theme.palette.error.dark
        }
      >
        <Currency value={product.pricing.grossPriceMarginal}>
          {`(${(product.pricing.grossPriceMarginalPercentage * 100).toFixed(
            2
          )} %)`}{" "}
          {directionIcon}
        </Currency>
      </Box>
    );
  };

  return (
    <>
      <Grid container spacing={2} direction={mobile ? "column" : "row"}>
        <Grid item xs={12} lg={4}>
          <Card className={classes.fullHeight}>
            <CardContent className={classes.fullHeight}>
              <Box position={"relative"}>
                <Tooltip
                  title={t("common.copyGtin")}
                  style={{
                    marginLeft: "0.3rem",
                    position: "absolute",
                    right: 0,
                    top: "-0.5rem"
                  }}
                >
                  <IconButton
                    edge="start"
                    size={"small"}
                    color="inherit"
                    onClick={e => {
                      navigator.clipboard.writeText(product.gtin).then(_ =>
                        setCopyToClipboardNotification({
                          open: true,
                          msg: t("common.productDetails.gtinCopiedToClipboard")
                        })
                      );
                    }}
                    aria-label={t("common.tooltip.copy")}
                  >
                    <ContentCopy style={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                display={"flex"}
                alignItems={"center"}
                height={"100%"}
                flexGrow={1}
              >
                <Box
                  py={mobile ? "1rem" : "auto"}
                  sx={{
                    display: "flex",
                    flexDirection: mobile ? "column" : "row",
                    justifyContent: "space-around",
                    flexGrow: 1
                  }}
                  pb={3}
                >
                  <Box pb={mobile ? "2rem" : "auto"}>
                    <CardMedia
                      style={{ maxHeight: theme.spacing(17) }}
                      component={"img"}
                      image={product.imageUrl}
                      title={product.name}
                    />
                  </Box>
                  <Box className={classes.fullHeight}>
                    <Barcode
                      gtin={product.gtin}
                      style={{ maxHeight: theme.spacing(15) }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card className={classes.fullHeight}>
            <CardContent>
              <Box sx={{ display: "flex" }} pb={3}>
                <Box flexGrow={1}>
                  <div>
                    <Typography
                      variant={"caption"}
                      color={"textSecondary"}
                      style={{ textTransform: "uppercase" }}
                    >
                      {t("common.productDetails.pricing")}
                    </Typography>
                  </div>
                  <Typography variant={"h6"}>
                    <Currency
                      value={product.pricing.price}
                      priceUnit={product.pricing.priceUnit}
                    />
                  </Typography>
                  {product.pricing.depositFee?.depositAmount &&
                    product.pricing.depositFee?.depositAmount > 0 && (
                      <span>
                        +
                        <Currency
                          value={product.pricing.depositFee.depositAmount}
                          priceUnit={product.pricing.priceUnit}
                        />
                      </span>
                    )}
                </Box>
                <div>
                  <AttachMoney className={classes.sectionIconSuccess} />
                </div>
              </Box>
              {renderProductDetails(
                t("common.productDetails.vat"),
                product.pricing.vatPercentage >= 0 ? (
                  <Currency value={product.pricing.vatAmount}>{`(${(
                    product.pricing.vatPercentage * 100
                  ).toFixed(0)} %)`}</Currency>
                ) : (
                  t("common.productDetails.cannotBeCalculated")
                )
              )}
              {renderProductDetails(
                t("common.productDetails.comparisonPrice"),
                product.pricing.comparisonPrice > 0 ? (
                  <Currency
                    value={product.pricing.comparisonPrice}
                    priceUnit={product.pricing.comparisonPriceUnit}
                  />
                ) : (
                  t("common.productDetails.cannotBeCalculated")
                )
              )}
              {renderProductDetails(
                t("common.productDetails.acquisitionPrice"),
                <Currency
                  value={product.pricing.acquisitionPrice}
                  priceUnit={product.pricing.priceUnit}
                />
              )}
              {renderProductDetails(
                t("common.productDetails.grossMarginal"),
                renderGrossMarginal(product)
              )}
              {product.pricing.depositFee?.depositAmount &&
              product.pricing.depositFee?.depositAmount > 0 ? (
                renderProductDetails(
                  t("common.productDetails.depositFee"),
                  <Currency
                    value={product.pricing.depositFee.depositAmount}
                    priceUnit={product.pricing.priceUnit}
                  />
                )
              ) : (
                <></>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card className={classes.fullHeight}>
            <CardContent>
              <Box sx={{ display: "flex" }} pb={3}>
                <Box flexGrow={1}>
                  {product.onShelf !== undefined ? (
                    <>
                      <div>
                        <Typography
                          variant={"caption"}
                          color={"textSecondary"}
                          style={{ textTransform: "uppercase" }}
                        >
                          {t("common.productDetails.onShelf")}
                        </Typography>
                      </div>
                      <Typography
                        variant={"h6"}
                        style={{ color: theme.palette.primary.main }}
                      >
                        {product.onShelf} {t("common.productDetails.pieces")}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <div>
                        <Typography
                          variant={"caption"}
                          color={"textSecondary"}
                          style={{ textTransform: "uppercase" }}
                        >
                          {t("common.productDetails.ageRestricted")}
                        </Typography>
                      </div>
                      <Typography variant={"h6"}>
                        {product.ageRestricted
                          ? t("common.productDetails.yes")
                          : t("common.productDetails.no")}
                      </Typography>
                    </>
                  )}
                </Box>
                <div>
                  <Info className={classes.sectionIconPrimary} />
                </div>
              </Box>
              {product.onShelf !== undefined &&
                renderProductDetails(
                  t("common.productDetails.ageRestricted"),
                  <>
                    {product.pricing.ageRestricted || product.ageRestricted
                      ? t("common.productDetails.yes")
                      : t("common.productDetails.no")}
                  </>
                )}
              {product.ageRestricted && (
                <>
                  {renderProductDetails(
                    t("common.productDetails.minimumAge"),
                    product.minimumAge
                  )}
                  {renderProductDetails(
                    t("common.productDetails.manualVerification"),
                    <>
                      {product.manualVerification
                        ? t("common.productDetails.yes")
                        : t("common.productDetails.no")}
                    </>
                  )}
                </>
              )}

              {renderProductDetails(
                t("common.productDetails.openPricing"),
                <>
                  {product.pricing.isOpenPricing
                    ? t("common.productDetails.yes")
                    : t("common.productDetails.no")}
                </>
              )}
              {renderProductDetails(
                t("common.productDetails.articleNumber"),
                product.articleNumber ?? "..."
              )}
              {renderProductDetails(
                t("common.productDetails.brand"),
                product.brandName
              )}
              {renderProductDetails(
                t("common.productDetails.distributor"),
                product.orderInfo?.distributor?.name ?? "..."
              )}
              {renderProductDetails(
                t("common.productDetails.orderUnit"),
                product.orderInfo?.orderUnit === OrderUnit.Kfp
                  ? t("common.productDetails.stKfp")
                  : `${t("common.productDetails.dfp")} (${
                      product.orderInfo?.distributionPackageSize ?? "..."
                    } ${t("common.productDetails.pieces")})`
              )}
              {renderProductDetails(
                t("common.productDetails.priority"),
                product.priority?.name.toUpperCase() ?? "..."
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box pt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StoreTable product={product} />
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={copyToClipboardNotification.open}
        autoHideDuration={1000}
        onClose={() => setCopyToClipboardNotification({ open: false, msg: "" })}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
        TransitionComponent={props => <Slide {...props} direction="down" />}
      >
        <lab.Alert severity="success" style={{ width: "100%" }}>
          {copyToClipboardNotification.msg}
        </lab.Alert>
      </Snackbar>
    </>
  );
};
